import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../contexts/UserContext";
import { useAchievements } from "../contexts/AchievementContext";
import { calculateLevel, calculateProgress, getLevelDescription } from "../utils/levelSystem";
import apiService from "../services/api";
import "../styles/Profile.css";
import "../styles/Notifications.css";

const Profile = ({ sidebarOpen }) => {
  const navigate = useNavigate();
  const { user, updateUser } = useUser();
  const { isShowingAchievement, handleApiResponse } = useAchievements();
  const [isEditing, setIsEditing] = useState(false);
  const [userData, setUserData] = useState(user || {
    name: "Chef",
    email: "",
    level: "Level 1 Chef",
    streak: 0,
    points: 0,
    joinDate: "January 2024",
    bio: "Welcome to your cooking journey!",
    favoriteCuisine: "",
    cookingExperience: "",
    profilePicture: null
  });

  const [achievements, setAchievements] = useState([]);
  const [achievementsLoading, setAchievementsLoading] = useState(true);
  const [userStats, setUserStats] = useState({ points: 0, streak: 0 });
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [activeRewards, setActiveRewards] = useState([]);
  const [nowTick, setNowTick] = useState(Date.now());

  // Calculate level and progress using latest stats
  const currentLevel = calculateLevel(userStats.points || userData.points || 0);
  const progress = calculateProgress(userStats.points || userData.points || 0);

  // Helper function to show success message
  const showSuccess = (message) => {
    // If an achievement is being shown, delay the success message
    if (isShowingAchievement) {
      setTimeout(() => {
        showSuccess(message);
      }, 6000); // Wait 6 seconds (5 for achievement + 1 buffer)
      return;
    }
    
    setSuccessMessage(message);
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 5000);
  };

  // Update local state when user context changes
  useEffect(() => {
    if (user) {
      setUserData(user);
      setUserStats({
        points: user.points || 0,
        streak: user.streak || 0
      });
    }
  }, [user]);

  // Fetch latest user stats (same as Dashboard)
  useEffect(() => {
    const fetchUserStats = async () => {
      try {
        const response = await apiService.getActivityStats();
        setUserStats({
          points: response.stats.points || 0,
          streak: response.stats.streak || 0,
          hasCompletedToday: response.stats.hasCompletedToday || false
        });
        // Update userData with latest stats
        setUserData(prev => ({
          ...prev,
          points: response.stats.points || 0,
          streak: response.stats.streak || 0,
          hasCompletedToday: response.stats.hasCompletedToday || false
        }));
      } catch (error) {
        console.error('Failed to fetch user stats:', error);
      }
    };

    if (user && apiService.getToken()) {
      fetchUserStats();
    }
  }, [user]);

  // Load achievements
  useEffect(() => {
    const loadAchievements = async () => {
      try {
        const response = await apiService.getAchievements();
        // Filter to show only earned achievements
        const earnedAchievements = response.achievements.filter(achievement => achievement.isEarned);
        setAchievements(earnedAchievements);
      } catch (error) {
        console.error('Failed to load achievements:', error);
      }
      setAchievementsLoading(false);
    };

    if (user && apiService.getToken()) {
      loadAchievements();
    }
  }, [user]);

  useEffect(() => {
    const loadActiveRewards = async () => {
      try {
        const response = await apiService.getActiveRewards();
        setActiveRewards(response?.activeRewards || []);
      } catch (error) {
        console.error('Failed to load active wheel rewards:', error);
        setActiveRewards([]);
      }
    };

    if (user && apiService.getToken()) {
      loadActiveRewards();
    }
  }, [user]);

  useEffect(() => {
    const timer = setInterval(() => {
      setNowTick(Date.now());
    }, 30000);

    return () => clearInterval(timer);
  }, []);

  const formatRewardTimeLeft = (expiresAt) => {
    if (!expiresAt) return 'No expiration';
    const ms = new Date(expiresAt).getTime() - nowTick;
    if (ms <= 0) return 'Expired';

    const hours = Math.floor(ms / (1000 * 60 * 60));
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m left`;
  };


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Bio character limit validation
    if (name === 'bio' && value.length > 500) {
      showSuccess('Bio cannot exceed 500 characters. Please shorten your bio.');
      return;
    }
    
    setUserData({
      ...userData,
      [name]: value
    });
  };

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showSuccess('Please select a valid image file.');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      showSuccess('Image size must be less than 5MB.');
      return;
    }

    setIsUploading(true);
    
    try {
      const result = await apiService.uploadProfilePicture(file);
      
      // Update user profile in database with ONLY the profile picture
      try {
        // Only send the profilePicture field to avoid validation issues
        const profileResponse = await apiService.updateProfile({ profilePicture: result.imageUrl });
        
        // Update local state only - don't call updateUserContext as it makes another API call
        setUserData(prevData => ({
          ...prevData,
          profilePicture: result.imageUrl
        }));
        
        // Dispatch custom event to notify Dashboard of profile picture task completion
        window.dispatchEvent(new CustomEvent('profilePictureTaskCompleted'));
        
        handleApiResponse(profileResponse);
      } catch (updateError) {
        console.error('Profile update error:', updateError);
        showSuccess('Picture uploaded but failed to save to profile. Please try again.');
        return;
      }
      
      showSuccess('Profile picture updated successfully!');
    } catch (error) {
      console.error('Profile picture upload error:', error);
      showSuccess('Failed to upload profile picture. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };


  const handleSave = async () => {
    try {
      // Validate bio length before saving
      if (userData.bio && userData.bio.length > 500) {
        showSuccess('Bio cannot exceed 500 characters. Please shorten your bio.');
        return;
      }
      
      // Check if bio was changed from default
      const originalBio = user?.bio || "Welcome to your cooking journey!";
      const bioChanged = userData.bio !== originalBio && userData.bio.trim() !== "";

      const profileUpdatePayload = {
        name: userData.name,
        email: userData.email,
        bio: userData.bio,
        favoriteCuisine: userData.favoriteCuisine || null,
        cookingExperience: userData.cookingExperience || null
      };
      
      // Update user via API
      const response = await updateUser(profileUpdatePayload);
      
      // The backend now handles activity logging for bio updates
      // Dispatch event and show appropriate message
      if (bioChanged) {
        showSuccess('Profile updated successfully! +8 points earned! 🎉');
        
        // Dispatch custom event to notify Dashboard of bio task completion
        window.dispatchEvent(new CustomEvent('bioTaskCompleted'));
      } else {
        showSuccess('Profile updated successfully!');
      }
      
      // Don't call updateUserContext as it makes another duplicate API call
      // User context will be refreshed on next page load
      setIsEditing(false);
      console.log('Profile updated successfully:', response);
    } catch (error) {
      console.error('Error updating user:', error);
      showSuccess('Failed to update profile. Please try again.');
    }
  };


  return (
    <div className={`profile-container ${sidebarOpen ? 'sidebar-open' : ''}`}>
      {/* Header */}
      <div className="profile-header">
        <div className="profile-header-content">
          <div>
            <h1 className="profile-title">My Profile</h1>
            <p className="profile-subtitle">Manage your account and cooking preferences</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="profile-content">
        {/* Profile Card */}
        <div className="profile-card">
          <div className="profile-avatar-section">
            <div className="profile-avatar-container">
              {userData.profilePicture ? (
                <img 
                  src={apiService.getImageUrl(userData.profilePicture)} 
                  alt="Profile" 
                  className="profile-avatar-image"
                />
              ) : (
                <div className="profile-avatar">{userData.name?.charAt(0) || "U"}</div>
              )}
              {/* Profile picture upload - always visible */}
              <div className="profile-picture-upload">
                <input
                  type="file"
                  id="profile-picture-input"
                  accept="image/*"
                  onChange={handleProfilePictureUpload}
                  style={{ display: 'none' }}
                  disabled={isUploading}
                />
                <label 
                  htmlFor="profile-picture-input" 
                  className="upload-profile-picture-btn"
                  title="Upload/Change profile picture"
                >
                  {isUploading ? '⏳' : '📷'}
                </label>
              </div>
            </div>
            <div className="profile-info">
              <div className="profile-name-with-badges">
                <h2 className="profile-name">
                  {userData.name}
                  {userData.discriminator && (
                    <span className="user-code"> #{userData.discriminator}</span>
                  )}
                </h2>
                <div className="user-badges">
                  {userStats.streak >= 7 && (
                    <div className="user-badge streak-badge" title="7+ Day Streak Master">
                      <div className="badge-placeholder">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z" fill="currentColor"/>
                        </svg>
                      </div>
                    </div>
                  )}
                  {currentLevel.level >= 10 && (
                    <div className="user-badge level-badge" title="Level 10+ Legend">
                      <div className="badge-placeholder">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2L15.09 8.26L22 9L15.09 9.74L12 16L8.91 9.74L2 9L8.91 8.26L12 2Z" fill="currentColor"/>
                        </svg>
                      </div>
                    </div>
                  )}
                  {userStats.points >= 10000 && (
                    <div className="user-badge points-badge" title="10K+ Points Collector">
                      <div className="badge-placeholder">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z" fill="currentColor"/>
                        </svg>
                      </div>
                    </div>
                  )}
                  {achievements.length >= 10 && (
                    <div className="user-badge achievement-badge" title="10+ Achievements">
                      <div className="badge-placeholder">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z" fill="currentColor"/>
                        </svg>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="level-display">
                <div className="level-info">
                  <p className="profile-level">{currentLevel.name}</p>
                  <p className="level-description">{getLevelDescription(currentLevel.level)}</p>
                </div>
              </div>
              <div className="profile-stats">
                <span className="stat-item">🔥 {userStats.streak || userData.streak} day streak</span>
                <span className="stat-item">⭐ {userStats.points || userData.points} points</span>
              </div>
              {progress.nextLevel && (
                <div className="level-progress">
                  <div className="progress-info">
                    <span>Progress to {progress.nextLevel.name}</span>
                    <span>{progress.percentage}%</span>
                  </div>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ 
                        width: `${progress.percentage}%`,
                        backgroundColor: currentLevel.color 
                      }}
                    ></div>
                  </div>
                  <div className="points-to-next">
                    {progress.pointsToNext} points to next level
                  </div>
                </div>
              )}
              <div className="profile-reward-strip">
                <h4>Active Wheel Rewards</h4>
                {activeRewards.length > 0 ? (
                  <div className="profile-reward-list">
                    {activeRewards.map((reward) => {
                      const isBoost = reward.rewardType === 'boost';
                      const title = isBoost ? '2x Points Active' : 'Streak Shield Active';
                      const detail = isBoost
                        ? 'Activity points are doubled until end of day.'
                        : 'Automatically saves your streak if you miss one day.';

                      return (
                        <div key={reward.id} className={`profile-reward-pill ${isBoost ? 'boost' : 'shield'}`}>
                          <span className="reward-pill-title">{title}</span>
                          <span className="reward-pill-time">{formatRewardTimeLeft(reward.expiresAt)}</span>
                          <span className="reward-pill-detail">{detail}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="profile-reward-empty">No active wheel rewards right now.</p>
                )}
              </div>
            </div>
            <button 
              className="edit-btn"
              onClick={() => setIsEditing(!isEditing)}
            >
              {isEditing ? "Cancel" : "Edit Profile"}
            </button>
          </div>

          {/* Profile Details */}
          <div className="profile-details">
            <div className="detail-section">
              <h3>Personal Information</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Full Name</label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="name"
                      value={userData.name}
                      onChange={handleInputChange}
                      className="profile-input"
                    />
                  ) : (
                    <span>{userData.name}</span>
                  )}
                </div>
                <div className="detail-item">
                  <label>Email</label>
                  {isEditing ? (
                    <input
                      type="email"
                      name="email"
                      value={userData.email}
                      onChange={handleInputChange}
                      className="profile-input"
                    />
                  ) : (
                    <span>{userData.email}</span>
                  )}
                </div>
                <div className="detail-item">
                  <label>Member Since</label>
                  <span className="read-only-field">
                    {userData.joinDate ? new Date(userData.joinDate).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    }) : 'Unknown'}
                  </span>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h3>Cooking Preferences</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Bio</label>
                  {isEditing ? (
                    <div className="bio-input-container">
                      <textarea
                        name="bio"
                        value={userData.bio}
                        onChange={handleInputChange}
                        className="profile-textarea"
                        rows="3"
                        maxLength="500"
                      />
                      <div className={`character-counter ${userData.bio && userData.bio.length > 450 ? 'warning' : ''}`}>
                        {userData.bio ? userData.bio.length : 0}/500 characters
                      </div>
                    </div>
                  ) : (
                    <span className="bio-display">{userData.bio}</span>
                  )}
                </div>
                <div className="detail-item">
                  <label>Favorite Cuisine</label>
                  {isEditing ? (
                    <select
                      name="favoriteCuisine"
                      value={userData.favoriteCuisine}
                      onChange={handleInputChange}
                      className="profile-select"
                    >
                      <option value="">Select favorite cuisine</option>
                      <option value="Italian">Italian</option>
                      <option value="Asian">Asian</option>
                      <option value="Mexican">Mexican</option>
                      <option value="French">French</option>
                      <option value="Indian">Indian</option>
                      <option value="Mediterranean">Mediterranean</option>
                      <option value="American">American</option>
                      <option value="German">German</option>
                      <option value="Thai">Thai</option>
                      <option value="Chinese">Chinese</option>
                      <option value="Japanese">Japanese</option>
                      <option value="Korean">Korean</option>
                      <option value="Spanish">Spanish</option>
                      <option value="Greek">Greek</option>
                      <option value="Turkish">Turkish</option>
                      <option value="Lebanese">Lebanese</option>
                      <option value="Moroccan">Moroccan</option>
                      <option value="Brazilian">Brazilian</option>
                      <option value="Peruvian">Peruvian</option>
                      <option value="Other">Other</option>
                    </select>
                  ) : (
                    <span>{userData.favoriteCuisine || 'Not set'}</span>
                  )}
                </div>
                <div className="detail-item">
                  <label>Cooking Experience</label>
                  {isEditing ? (
                    <select
                      name="cookingExperience"
                      value={userData.cookingExperience}
                      onChange={handleInputChange}
                      className="profile-select"
                    >
                      <option value="">Select cooking experience</option>
                      <option value="Beginner">Beginner</option>
                      <option value="Intermediate">Intermediate</option>
                      <option value="Advanced">Advanced</option>
                      <option value="Expert">Expert</option>
                    </select>
                  ) : (
                    <span>{userData.cookingExperience || 'Not set'}</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {isEditing && (
            <div className="profile-actions">
              <button className="save-btn" onClick={handleSave}>
                Save Changes
              </button>
            </div>
          )}
        </div>

        {/* Achievement Section */}
        <div className="achievement-section">
          <h3>Achievements</h3>
          {achievementsLoading ? (
            <div className="loading">Loading achievements...</div>
          ) : achievements.length > 0 ? (
            <>
              <div className="achievement-grid">
                {achievements.slice(0, 4).map((achievement, index) => (
                  <div key={index} className="achievement-card unlocked">
                    <div className={`achievement-icon ${
                      achievement.pointsReward >= 100 ? 'gold' : 
                      achievement.pointsReward >= 50 ? 'silver' : 'bronze'
                    }`}>
                      {achievement.icon}
                    </div>
                    <h4>{achievement.name}</h4>
                    <p>{achievement.description}</p>
                    <div className="achievement-reward">+{achievement.pointsReward} points</div>
                    {achievement.earnedAt && (
                      <div className="achievement-date">
                        Earned: {new Date(achievement.earnedAt).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {achievements.length > 4 && (
                <div className="view-all-achievements">
                  <button 
                    className="view-all-btn"
                    onClick={() => navigate('/achievements')}
                  >
                    View All {achievements.length} Achievements →
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="no-achievements">
              <div className="no-achievements-icon">🏆</div>
              <h4>No achievements yet</h4>
              <p>Start cooking and completing activities to earn your first achievement!</p>
            </div>
          )}
        </div>

      </div>

      {/* Achievement-Style Success Notification */}
      {showSuccessMessage && (
        <div className="success-notification">
          <div className="success-content">
            <span className="success-icon">✅</span>
            <div className="success-text">
              <strong>Success!</strong>
              <span>{successMessage}</span>
            </div>
            <button className="success-close" onClick={() => setShowSuccessMessage(false)}>×</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
